import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ALabel, ALabelSize } from '@marriott/mi-web-ui-kit';
import aiSparkleIconImport from '../../organisms/ONavigationSide/sparkle.png';
import { PillPosition, MMapPinHoverOverlayProps } from './MMapPinHoverOverlay.types';
import * as Styled from './MMapPinHoverOverlay.styles';

const PILL_MAX_WIDTH = 266;
const PILL_HEIGHT = 50;
const MARKER_HEIGHT = 24;
const EDGE_PADDING = 10;

const aiSparkleIconUrl =
  typeof aiSparkleIconImport === 'string' ? aiSparkleIconImport : (aiSparkleIconImport as { src: string }).src;

const calculatePillPosition = (
  markerPixelX: number,
  markerPixelY: number,
  mapWidth: number,
  mapHeight: number
): PillPosition => {
  const hasSpaceOnTop = markerPixelY > PILL_HEIGHT + EDGE_PADDING;
  const hasSpaceOnBottom = mapHeight - markerPixelY > MARKER_HEIGHT + PILL_HEIGHT + EDGE_PADDING;

  const vertical: 'top' | 'bottom' = hasSpaceOnTop ? 'top' : hasSpaceOnBottom ? 'bottom' : 'top';

  const halfPillWidth = PILL_MAX_WIDTH / 2;
  const hasSpaceOnLeft = markerPixelX > halfPillWidth + EDGE_PADDING;
  const hasSpaceOnRight = mapWidth - markerPixelX > halfPillWidth + EDGE_PADDING;

  let horizontal: 'center' | 'left' | 'right' = 'center';
  if (!hasSpaceOnLeft && hasSpaceOnRight) {
    horizontal = 'left';
  } else if (hasSpaceOnLeft && !hasSpaceOnRight) {
    horizontal = 'right';
  }

  return { vertical, horizontal };
};

// Convert lat/lng to pixel coordinates within the map container.
// Mirrors the helper in MSmartInfoWindow.
const getMarkerPixelPosition = (map: google.maps.Map, lat: number, lng: number): { x: number; y: number } | null => {
  const bounds = map.getBounds();
  const projection = map.getProjection();

  if (!bounds || !projection) return null;

  const mapDiv = map.getDiv();
  if (!mapDiv) return null;

  const mapWidth = mapDiv.offsetWidth;
  const mapHeight = mapDiv.offsetHeight;

  const markerLatLng = new google.maps.LatLng(lat, lng);
  const topRight = projection.fromLatLngToPoint(bounds.getNorthEast());
  const bottomLeft = projection.fromLatLngToPoint(bounds.getSouthWest());
  const markerPoint = projection.fromLatLngToPoint(markerLatLng);

  if (!topRight || !bottomLeft || !markerPoint) return null;

  const worldWidth = topRight.x - bottomLeft.x;
  const worldHeight = bottomLeft.y - topRight.y;

  const x = ((markerPoint.x - bottomLeft.x) / worldWidth) * mapWidth;
  const y = ((markerPoint.y - topRight.y) / worldHeight) * mapHeight;

  return { x, y };
};

// Hover-state visual for a map marker. Portaled into a positioned ancestor
// of the map (typically ChatMapContainer) so it renders above clusters,
// filter chips, and the mobile list toggle.
//
// Purely visual: pointer-events: none on every node. The real google.maps.Marker
// stays beneath and owns hover/click, which keeps the lifecycle race-free.
export const MMapPinHoverOverlay: React.FC<MMapPinHoverOverlayProps> = ({
  marker,
  map,
  portalContainer,
  isSinglePropertyResponse,
}) => {
  const [position, setPosition] = useState<PillPosition>({ vertical: 'top', horizontal: 'center' });
  const [pixelPosition, setPixelPosition] = useState<{ x: number; y: number } | null>(null);

  const hasPrice = !!marker.priceLabel;
  const labelText = hasPrice ? marker.priceLabel! : marker.rateUnavailableLabel || 'N/A';

  const markerHalfWidth = useMemo(() => {
    // Single property view marker is a fixed-size circle dot.
    if (isSinglePropertyResponse) {
      return 9;
    }
    const priceText = marker.priceLabel || '$000';
    const baseBuffer = marker.isAIRecommended ? 24 : 16;
    const estimatedWidth = baseBuffer + priceText.length * 7;
    return Math.round(estimatedWidth / 2);
  }, [marker.priceLabel, marker.isAIRecommended, isSinglePropertyResponse]);

  const updatePosition = useCallback(() => {
    if (!map) return;

    const pixelPos = getMarkerPixelPosition(map, marker.latitude, marker.longitude);
    if (!pixelPos) return;

    const mapDiv = map.getDiv();
    if (!mapDiv) return;

    const mapWidth = mapDiv.offsetWidth;
    const mapHeight = mapDiv.offsetHeight;

    setPixelPosition(pixelPos);
    setPosition(calculatePillPosition(pixelPos.x, pixelPos.y, mapWidth, mapHeight));
  }, [map, marker.latitude, marker.longitude]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  // Track marker position during pan/zoom.
  useEffect(() => {
    if (!map) return;

    const listeners: google.maps.MapsEventListener[] = [
      map.addListener('bounds_changed', updatePosition),
      map.addListener('zoom_changed', updatePosition),
      map.addListener('center_changed', updatePosition),
      map.addListener('idle', updatePosition),
    ];

    return () => {
      listeners.forEach(listener => google.maps.event.removeListener(listener));
    };
  }, [map, updatePosition]);

  useEffect(() => {
    const handleResize = () => updatePosition();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [updatePosition]);

  if (!map || !portalContainer || !pixelPosition) return null;

  const pillClassName = [marker.isAIRecommended ? 'ai-recommended' : '', isSinglePropertyResponse ? 'circle' : '']
    .filter(Boolean)
    .join(' ');

  return ReactDOM.createPortal(
    <Styled.HoverOverlayPortalContainer
      $markerPixelX={pixelPosition.x}
      $markerPixelY={pixelPosition.y}
      data-testid="map-pin-hover-overlay"
    >
      <Styled.HoverPricePinClone
        className={pillClassName}
        $aiSparkleIconUrl={aiSparkleIconUrl}
        data-testid="map-pin-hover-pill"
      >
        {isSinglePropertyResponse ? '' : labelText}
      </Styled.HoverPricePinClone>
      {/* The hotel-name pill shows on hover for all views. For single property
          view the pin itself is a circle (no baked-in title); the full title
          still appears here on hover. */}
      <Styled.HoverOverlayContainer
        $vertical={position.vertical}
        $horizontal={position.horizontal}
        $markerHalfWidth={markerHalfWidth}
      >
        <Styled.PropertyNamePill>
          <ALabel size={ALabelSize.XS} text={marker.name} />
        </Styled.PropertyNamePill>
      </Styled.HoverOverlayContainer>
    </Styled.HoverOverlayPortalContainer>,
    portalContainer
  );
};

export default MMapPinHoverOverlay;
